import React, { useEffect, useState, useMemo } from "react";
import { FaCamera, FaEdit, FaSave, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { IMAGE_URL } from "../Utils/baseUrl";
import { useNavigate } from "react-router-dom";
import { decryptData, encryptData } from "../Utils/encryption";
import { Divider, Modal } from "@mui/material";
import { updateUser } from "../Redux/Slice/user.slice";
import { AiOutlineClose } from "react-icons/ai";
import { motion } from "framer-motion";

const EditProfile = () => {
  const userId = localStorage.getItem("yoyouserId");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user.currUser);

  const [isEditing, setIsEditing] = useState(false);
  const [originalValues, setOriginalValues] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    if (isModalOpen) {
      const scrollY = window.scrollY;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
    } else {
      const scrollY = document.body.style.top;
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      if (scrollY) {
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      }
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
    };
  }, [isModalOpen]);

  const formik = useFormik({
    initialValues: {
      userName: "",
      fullName: "",
      email: "",
      photo: "",
    },
    onSubmit: (values) => {
      const encryptedValues = {
        userName: values.userName ? encryptData(values.userName) : "",
        fullName: values.fullName ? encryptData(values.fullName) : "",
        email: values.email ? encryptData(values.email) : "",
        photo: values.photo || "",
      };

      dispatch(
        updateUser({
          id: userId,
          values: encryptedValues,
          file: selectedFile,
        })
      ).then((response) => {
        if (response.payload?.success) {
          setIsEditing(false);
          setOriginalValues(values);
          navigate("/profile");
          setSelectedFile(null);
          setPreviewUrl(null);
          setImageRemoved(false);
        }
      });
    },
  });

  const { handleSubmit, setFieldValue, handleChange, setValues, values } =
    formik;

  const isChanged = useMemo(() => {
    return (
      JSON.stringify(values) !== JSON.stringify(originalValues) ||
      selectedFile !== null ||
      imageRemoved
    );
  }, [values, originalValues, selectedFile, imageRemoved]);

  useEffect(() => {
    if (currentUser) {
      const userData = {
        userName: decryptData(currentUser.userName) || "",
        fullName: decryptData(currentUser.fullName) || "",
        email: decryptData(currentUser.email) || "",
        photo: currentUser.photo || "",
      };
      setValues(userData);
      setOriginalValues(userData);
    }
  }, [currentUser, setValues]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setImageRemoved(false);
      setIsEditing(true);

      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result);
        setFieldValue("photo", reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setImageRemoved(true);
    setFieldValue("photo", "null");
  };

  const avatars = [
    require("../Asset/images/1.png"),
    require("../Asset/images/2.png"),
    require("../Asset/images/3.png"),
    require("../Asset/images/4.png"),
    require("../Asset/images/5.png"),
    require("../Asset/images/6.png"),
    require("../Asset/images/7.png"),
    require("../Asset/images/8.png"),
  ];

  const handleAvatarSelect = async (avatar) => {
    try {
      const response = await fetch(avatar);
      const blob = await response.blob();
      const fileName = `avatar-${Date.now()}.png`;
      const file = new File([blob], fileName, { type: blob.type });

      setIsEditing(true);
      setPreviewUrl(avatar);
      setSelectedFile(file);
      setFieldValue("photo", file);
      setImageRemoved(false);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Error selecting avatar:", error);
    }
  };

  const handleEditClick = () => {
    if (!isEditing) {
      setIsEditing(true);
    } else {
      if (isChanged) {
        handleSubmit();
      } else {
        setValues(originalValues);
        setIsEditing(false);
      }
    }
  };

  return (
    <div className="relative overflow-hidden bg-[#1a1a1a]/60 backdrop-blur-xl border border-white/10 rounded-3xl sm:rounded-[40px] shadow-2xl p-5 sm:p-8 lg:p-12 xl:p-16">
      {/* Subtle Background Glows - Adjusted for better mobile fit */}
      <div className="absolute -top-12 -right-12 sm:-top-24 sm:-right-24 w-48 sm:w-64 h-48 sm:h-64 bg-[#6263ED]/10 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />
      <div className="absolute -bottom-12 -left-12 sm:-bottom-24 sm:-left-24 w-48 sm:w-64 h-48 sm:h-64 bg-[#A7006F]/10 rounded-full blur-[80px] sm:blur-[100px] pointer-events-none" />

      <form onSubmit={handleSubmit} className="relative z-10">
        {/* Header Section - Better alignment on mobile */}
        <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start gap-6 sm:gap-10 mb-8 sm:mb-12 pb-8 sm:pb-12 border-b border-white/10">
          <div className="relative group">
            <div className="w-24 h-24 sm:w-28 sm:h-28 lg:w-32 lg:h-32 rounded-2xl sm:rounded-3xl overflow-hidden border-2 border-white/10 shadow-2xl relative group-hover:border-[#6263ED]/50 transition-all duration-300">
              {previewUrl ? (
                <img src={previewUrl} alt="Profile" className="w-full h-full object-cover" />
              ) : !imageRemoved && currentUser?.photo && currentUser?.photo !== "null" ? (
                <img
                  src={currentUser.photo.startsWith("/uploads/") ? `${IMAGE_URL}${currentUser.photo}` : currentUser.photo}
                  alt="Profile"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gradient-primary flex items-center justify-center">
                  <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase">
                    {decryptData(currentUser?.fullName)?.[0] || decryptData(currentUser?.userName)?.[0]}
                  </span>
                </div>
              )}

              {/* Change Overlay */}
              <div
                onClick={() => setIsModalOpen(true)}
                className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 flex flex-col items-center justify-center transition-all duration-300 backdrop-blur-sm rounded-2xl sm:rounded-3xl cursor-pointer"
              >
                <FaCamera className="text-white text-xl sm:text-2xl mb-1" />
                <span className="text-[10px] font-bold text-white uppercase tracking-wider">Change</span>
              </div>
            </div>

            {/* Remove Image Button */}
            {(previewUrl || (!imageRemoved && currentUser?.photo && currentUser?.photo !== "null")) && (
              <button
                type="button"
                onClick={handleRemoveImage}
                className="absolute -top-1 -right-1 sm:-top-2 sm:-right-2 w-7 h-7 sm:w-8 sm:h-8 bg-red-500 text-white rounded-lg sm:rounded-xl shadow-lg flex items-center justify-center transition-all duration-300 z-20"
              >
                <FaTimes size={14} />
              </button>
            )}
          </div>

          <div className="flex-1 flex flex-col items-center sm:items-end gap-3 sm:gap-4 text-center sm:text-right w-full sm:w-auto">
            <div>
              <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white mb-1">
                {decryptData(currentUser?.fullName) || "User Profile"}
              </h2>
              <p className="text-gray-400 text-xs sm:text-sm font-medium">
                Manage your account settings and profile details.
              </p>
            </div>
            <button
              type={isChanged && isEditing ? "submit" : "button"}
              onClick={!isChanged || !isEditing ? handleEditClick : undefined}
              className="w-full sm:w-auto px-6 sm:px-10 py-3 sm:py-4 rounded-xl sm:rounded-2xl bg-gradient-primary text-white font-bold text-xs sm:text-sm shadow-lg hover:shadow-[#6263ED]/20 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 flex items-center justify-center gap-2"
            >
              {!isEditing ? (
                <>
                  <FaEdit className="text-base sm:text-lg" />
                  Edit Profile
                </>
              ) : isChanged ? (
                <>
                  <FaSave className="text-base sm:text-lg" />
                  Save Changes
                </>
              ) : (
                <>
                  <FaTimes className="text-base sm:text-lg" />
                  Cancel
                </>
              )}
            </button>
          </div>
        </div>

        {/* Form Fields - Grid adjustments for different screens */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-8">
          <div className="space-y-2 sm:space-y-3">
            <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Full Name</label>
            <input
              name="fullName"
              type="text"
              placeholder="Enter your full name"
              className={`w-full bg-white/5 border ${!isEditing ? 'border-white/5 opacity-70' : 'border-white/10 focus:border-[#6263ED]/50 focus:bg-white/[0.08]'} text-xs sm:text-sm text-white px-5 sm:px-6 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl outline-none transition-all duration-300`}
              onChange={handleChange}
              value={values.fullName}
              disabled={!isEditing}
            />
          </div>

          <div className="space-y-2 sm:space-y-3">
            <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">User Name</label>
            <input
              name="userName"
              type="text"
              placeholder="Username"
              className="w-full bg-white/5 border border-white/5 text-xs sm:text-sm text-white px-5 sm:px-6 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl outline-none opacity-70 cursor-not-allowed"
              value={values.userName}
              disabled
            />
          </div>

          <div className="space-y-2 sm:space-y-3 md:col-span-2">
            <label className="text-[10px] sm:text-xs font-bold text-gray-500 uppercase tracking-widest ml-1">Email Address</label>
            <div className="relative group">
              <input
                name="email"
                type="email"
                className="w-full bg-white/5 border border-white/5 text-xs sm:text-sm text-white px-5 sm:px-6 py-3.5 sm:py-5 rounded-xl sm:rounded-2xl outline-none opacity-70 cursor-not-allowed pr-24"
                value={values.email}
                readOnly
                disabled
              />
              <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center gap-1.5 opacity-40">
                <span className="text-[9px] font-bold text-white uppercase">Linked</span>
                <div className="w-1.5 h-1.5 rounded-full bg-white" />
              </div>
            </div>
          </div>
        </div>
      </form>

      {/* Avatar Modal - Responsive adjustments */}
      <Modal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        className="fixed inset-0 flex items-center justify-center z-[100] px-4"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-[#1a1a1a]/95 backdrop-blur-2xl border border-white/10 p-6 sm:p-10 text-white w-full max-w-[500px] rounded-[32px] sm:rounded-[48px] shadow-2xl relative overflow-hidden outline-none"
        >
          <div className="absolute -top-24 -right-24 w-48 h-48 bg-[#6263ED]/20 rounded-full blur-[80px]" />

          <div className="flex justify-between items-center mb-6 sm:mb-10 relative z-10">
            <div className="flex items-center gap-3 sm:gap-4">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl sm:rounded-2xl bg-gradient-primary flex items-center justify-center shadow-lg">
                <FaCamera className="text-white text-lg sm:text-xl" />
              </div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Select Avatar</h1>
            </div>
            <button
              onClick={() => setIsModalOpen(false)}
              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/5 flex items-center justify-center hover:bg-red-500/20 hover:text-red-500 transition-all duration-300"
            >
              <AiOutlineClose className="text-xl sm:text-2xl" />
            </button>
          </div>

          <div className="space-y-6 sm:space-y-10 relative z-10">
            <label
              htmlFor="modalProfileImageInput"
              className="flex flex-col items-center justify-center border-2 border-dashed border-white/10 rounded-[24px] sm:rounded-[32px] p-6 sm:p-10 hover:bg-white/5 hover:border-[#6263ED]/50 transition-all duration-300 cursor-pointer group"
            >
              <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-[#6263ED]/10 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <FaCamera className="text-[#6263ED] text-xl sm:text-2xl" />
              </div>
              <span className="text-white font-bold text-sm sm:text-base">Upload Custom Image</span>
              <span className="text-[10px] text-gray-500 font-medium mt-1">PNG, JPG, JPEG (Max 5MB)</span>
              <input
                id="modalProfileImageInput"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => {
                  handleImageUpload(e);
                  setIsModalOpen(false);
                }}
              />
            </label>

            <div className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <span className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.2em]">Quick Selection</span>
                <div className="h-[1px] flex-1 bg-white/5" />
              </div>

              <div className="grid grid-cols-4 gap-3 sm:gap-4 max-h-[220px] sm:max-h-[280px] overflow-y-auto pr-2 custom-scrollbar">
                {avatars.map((avatar, index) => (
                  <button
                    key={index}
                    onClick={() => handleAvatarSelect(avatar)}
                    className="rounded-xl sm:rounded-2xl overflow-hidden border-2 border-transparent hover:border-[#6263ED]/50 transition-all duration-300 aspect-square group shadow-lg"
                  >
                    <img src={avatar} alt={`Avatar ${index + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      </Modal>
    </div>
  );
};

export default EditProfile;
